import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import moment from 'moment';
import styles from './exchanges.module.scss';

/**
 * Interactive line chart for a composite index series.
 *
 * Hand-rolled SVG rather than a charting library: the bundle is already very
 * large, and this needs one line, one axis and a crosshair.
 *
 * It replaces a bare `<polyline preserveAspectRatio="none">`, which had three
 * problems — no interaction of any kind, a stroke that stretched with the
 * viewBox so the line thickened on wide screens, and no axis, so an outlier in
 * the data looked like a rendering glitch rather than a number.
 *
 * Reads the pointer, touch (via pointer events) and the keyboard: the plot is
 * focusable and arrow keys walk the series.
 */

const PAD = {top: 14, right: 16, bottom: 22, left: 46};
const MIN_HEIGHT = 180;

/** Nice-ish rounded bounds so the axis labels are readable numbers. */
const domainFor = values => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    // A flat series still needs a band, or every point lands on one pixel row.
    const pad = Math.abs(min) * 0.02 || 1;
    return [min - pad, max + pad];
  }
  const pad = (max - min) * 0.12;
  return [min - pad, max + pad];
};

const formatValue = value =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const IndexChart = ({series, color = '#8d6cfa', height = 220}) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState(null);

  // The SVG is drawn at the measured pixel width so the stroke is never
  // scaled. Falls back to a sane width before the first measurement.
  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const measure = () => setWidth(node.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const points = useMemo(
    () =>
      (series || [])
        .filter(point => Number.isFinite(Number(point?.value)))
        .map(point => ({
          value: Number(point.value),
          asOf: point.asOf ? moment(point.asOf) : null,
        })),
    [series],
  );

  const plotWidth = Math.max((width || 640) - PAD.left - PAD.right, 10);
  const plotHeight = Math.max(height - PAD.top - PAD.bottom, 10);

  const geometry = useMemo(() => {
    if (points.length < 2) return null;
    const values = points.map(point => point.value);
    const [low, high] = domainFor(values);
    const span = high - low || 1;
    const x = index => (index / (points.length - 1)) * plotWidth;
    const y = value => plotHeight - ((value - low) / span) * plotHeight;
    const line = points
      .map(
        (point, index) => `${x(index).toFixed(2)},${y(point.value).toFixed(2)}`,
      )
      .join(' ');
    return {low, high, x, y, line};
  }, [points, plotWidth, plotHeight]);

  const pick = useCallback(
    clientX => {
      if (!geometry || !wrapRef.current) return;
      const box = wrapRef.current.getBoundingClientRect();
      const offset = clientX - box.left - PAD.left;
      const ratio = Math.min(Math.max(offset / plotWidth, 0), 1);
      setActive(Math.round(ratio * (points.length - 1)));
    },
    [geometry, plotWidth, points.length],
  );

  const onKeyDown = event => {
    if (!geometry) return;
    const last = points.length - 1;
    const current = active ?? last;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(Math.max(current - 1, 0));
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(Math.min(current + 1, last));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(last);
    } else if (event.key === 'Escape') {
      setActive(null);
    }
  };

  if (!points.length)
    return <div className={styles.empty}>No index series published.</div>;

  if (!geometry)
    return (
      <div className={styles.chartSingle}>
        {formatValue(points[0].value)}
        {points[0].asOf && (
          <span> · {points[0].asOf.format('D MMM YYYY, HH:mm')}</span>
        )}
      </div>
    );

  const {low, high, x, y, line} = geometry;
  const mid = (low + high) / 2;
  const areaId = 'indexChartFill';
  const cursor = active == null ? null : points[active];
  const previous = active ? points[active - 1] : null;
  const delta = cursor && previous ? cursor.value - previous.value : null;

  // Keep the tooltip inside the plot rather than letting it run off an edge.
  const cursorX = cursor ? x(active) + PAD.left : 0;
  const tooltipSide = cursorX > (width || 640) * 0.6 ? 'right' : 'left';

  return (
    <div
      ref={wrapRef}
      className={styles.chartWrap}
      style={{height: Math.max(height, MIN_HEIGHT)}}
      tabIndex={0}
      role="img"
      aria-label={`Composite index, ${points.length} points, from ${formatValue(
        points[0].value,
      )} to ${formatValue(points[points.length - 1].value)}`}
      onKeyDown={onKeyDown}
      onPointerMove={event => pick(event.clientX)}
      onPointerDown={event => pick(event.clientX)}
      onPointerLeave={() => setActive(null)}
      onBlur={() => setActive(null)}>
      <svg width={width || '100%'} height={height} className={styles.chartSvg}>
        <defs>
          <linearGradient id={areaId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        <g transform={`translate(${PAD.left},${PAD.top})`}>
          {/* Gridlines and value labels — without them an outlier reads as a
              glitch instead of a number. */}
          {[high, mid, low].map(value => (
            <g key={value}>
              <line
                x1={0}
                x2={plotWidth}
                y1={y(value)}
                y2={y(value)}
                className={styles.chartGrid}
              />
              <text
                x={-10}
                y={y(value)}
                dy="0.32em"
                textAnchor="end"
                className={styles.chartAxisLabel}>
                {formatValue(value)}
              </text>
            </g>
          ))}

          <path
            d={`M0,${plotHeight} L${line
              .split(' ')
              .join(' L')} L${plotWidth},${plotHeight} Z`}
            fill={`url(#${areaId})`}
          />
          <polyline
            points={line}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {cursor && (
            <g>
              <line
                x1={x(active)}
                x2={x(active)}
                y1={0}
                y2={plotHeight}
                className={styles.chartCrosshair}
              />
              <circle
                cx={x(active)}
                cy={y(cursor.value)}
                r="4.5"
                fill="#fff"
                stroke={color}
                strokeWidth="2.5"
              />
            </g>
          )}

          {points[0].asOf && (
            <text x={0} y={plotHeight + 16} className={styles.chartAxisLabel}>
              {points[0].asOf.format('D MMM')}
            </text>
          )}
          {points[points.length - 1].asOf && (
            <text
              x={plotWidth}
              y={plotHeight + 16}
              textAnchor="end"
              className={styles.chartAxisLabel}>
              {points[points.length - 1].asOf.format('D MMM')}
            </text>
          )}
        </g>
      </svg>

      {cursor && (
        <div
          className={`${styles.chartTip} ${
            tooltipSide === 'right' ? styles.chartTipRight : ''
          }`}
          style={{left: cursorX}}>
          <span className={styles.chartTipValue}>
            {formatValue(cursor.value)}
          </span>
          {delta != null && (
            <span
              className={delta >= 0 ? styles.chartTipUp : styles.chartTipDown}>
              {delta >= 0 ? '+' : ''}
              {formatValue(delta)}
            </span>
          )}
          {cursor.asOf && (
            <span className={styles.chartTipDate}>
              {cursor.asOf.format('D MMM YYYY, HH:mm')}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default IndexChart;
